/**
 * Board — Top-Level-Komponente fuer das Kanban-Board.
 *
 * Verantwortlich fuer:
 *  - DndContext mit PointerSensor (activationConstraint distance:8 fuer
 *    Drag-vs-Click-Detection — Click oeffnet Modal, Drag verschiebt Karte)
 *  - 7 Spalten nebeneinander (BOARD-01)
 *  - FilterBar (BOARD-03 + BOARD-04)
 *  - onDragEnd dispatcht store.moveLead (BOARD-02)
 *  - Filter-Anwendung vor Sortierung (search/tags/user/dateRange UND-verknuepft)
 *  - Init: useEffect ruft store.init() (laedt leads + users + startet Polling)
 *  - Error-Toast bei Fehler aus dem Store
 *
 * CI-Farben: #E9FF57 / #1A1A1A / #DAD9D9, NIE weisse Schrift auf gelbem Background.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Inbox, AlertCircle, X } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import { COLUMNS } from '../lib/columns';
import { useLeadBoard } from '../store/useLeadBoard';
import { taskBadgeState } from '../lib/taskBadgeState';
import type { BoardFilters, Lead, LeadTask } from '../types';
import FilterBar from './FilterBar';
import Column from './Column';
import CardDetailModal from './CardDetailModal';
import CreateLeadModal from './CreateLeadModal';
import TasksOverviewPanel from './TasksOverviewPanel';
import BulkActionBar from './BulkActionBar';

/**
 * filterLeads — Pure helper. UND-verknuepfte Filter:
 *  - search: case-insensitive substring in vereinName + vereinEmail
 *  - tags: jeder ausgewaehlte Tag muss in lead.tags enthalten sein
 *  - assignedUserId: exact match
 *  - dateRange: receivedAt > now - X Tage (all = kein Filter)
 */
function filterLeads(leads: Lead[], filters: BoardFilters, now: Date): Lead[] {
  const search = filters.search.trim().toLowerCase();
  return leads.filter((lead) => {
    if (search.length > 0) {
      const inName = lead.vereinName.toLowerCase().includes(search);
      const inMail =
        lead.vereinEmail !== null && lead.vereinEmail.toLowerCase().includes(search);
      if (!inName && !inMail) return false;
    }
    if (filters.tags.length > 0) {
      const allTagsPresent = filters.tags.every((t) => lead.tags.includes(t));
      if (!allTagsPresent) return false;
    }
    if (filters.assignedUserId != null) {
      if (lead.assignedUserId !== filters.assignedUserId) return false;
    }
    if (filters.dateRange !== 'all') {
      const maxDays =
        filters.dateRange === '7d' ? 7 : filters.dateRange === '30d' ? 30 : 90;
      const days = differenceInDays(now, new Date(lead.receivedAt));
      if (days > maxDays) return false;
    }
    return true;
  });
}

export default function Board() {
  const leads = useLeadBoard((s) => s.leads);
  const users = useLeadBoard((s) => s.users);
  const filters = useLeadBoard((s) => s.filters);
  const setFilter = useLeadBoard((s) => s.setFilter);
  const moveLead = useLeadBoard((s) => s.moveLead);
  const init = useLeadBoard((s) => s.init);
  const stopPolling = useLeadBoard((s) => s.stopPolling);
  const error = useLeadBoard((s) => s.error);
  const clearError = useLeadBoard((s) => s.clearError);
  const loading = useLeadBoard((s) => s.loading);
  const tasksByLead = useLeadBoard((s) => s.tasksByLead);
  const taskFilterActive = useLeadBoard((s) => s.taskFilterActive);

  // "now" wird einmal pro Render als Referenz gewaehlt — Tests in pure functions
  // bekommen es als Argument. Polling rerendert das Board, dann ist now wieder frisch.
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    init();
    // Alle 60s "now" aktualisieren damit Deal-Rotting + Letzter-Kontakt
    // auch ohne Polling-Tick durchziehen.
    const tick = setInterval(() => setNow(new Date()), 60000);
    return () => {
      clearInterval(tick);
      stopPolling();
    };
    // init/stopPolling sind stable (zustand-actions), keine deps noetig
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sensors: PointerSensor mit activationConstraint distance:8 — verhindert
  // dass jeder Klick als Drag interpretiert wird.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const filtered = useMemo(() => {
    const base = filterLeads(leads, filters, now);
    if (!taskFilterActive) return base;
    // Zusatz-Filter: nur Karten mit heute-faelliger oder ueberfaelliger Task
    return base.filter((l) =>
      taskBadgeState(tasksByLead[l.id] as LeadTask[] | undefined, now),
    );
  }, [leads, filters, now, taskFilterActive, tasksByLead]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const l of leads) for (const t of l.tags) set.add(t);
    // System-Tag hotlead sicherstellen (auch wenn aktuell kein Lead es hat)
    set.add('hotlead');
    return Array.from(set).sort();
  }, [leads]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const leadId = (active.data.current as { leadId?: number } | undefined)?.leadId;
    const fromStatus = (active.data.current as { fromStatus?: Lead['status'] } | undefined)
      ?.fromStatus;
    const toStatus = (over.data.current as { status?: Lead['status'] } | undefined)?.status;
    if (leadId == null || toStatus == null) return;
    if (fromStatus === toStatus) return; // gleicher Spalten-Drop -> nichts tun
    moveLead(leadId, toStatus).catch(() => {
      /* Fehler wird ueber store.error angezeigt */
    });
  };

  return (
    <div className="p-6 bg-white min-h-screen text-[#1A1A1A]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Inbox className="w-8 h-8 text-[#1A1A1A]" />
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Leadmanagement Teamshops</h1>
        {loading && (
          <span className="text-xs text-gray-500 ml-2">Lade...</span>
        )}
      </div>

      {/* FilterBar + TasksOverviewPanel */}
      <div className="mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex-1">
          <FilterBar
            filters={filters}
            onChange={(patch) => setFilter(patch)}
            users={users}
            allTags={allTags}
          />
        </div>
        <div className="shrink-0">
          <TasksOverviewPanel />
        </div>
      </div>

      {/* Error-Toast */}
      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="text-sm flex-1">{error}</span>
          <button
            type="button"
            onClick={clearError}
            className="hover:bg-red-100 rounded p-0.5"
            aria-label="Fehler schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Board mit DndContext */}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <Column key={col.status} column={col} leads={filtered} now={now} />
          ))}
        </div>
      </DndContext>

      {/* Leerzustand */}
      {!loading && leads.length === 0 && (
        <div className="mt-6 p-4 bg-[#E9FF57]/30 border border-[#E9FF57] rounded text-[#1A1A1A]">
          <p className="font-medium">
            Noch keine Leads — Email an teamshops@anstoss24.de wird automatisch verarbeitet.
          </p>
        </div>
      )}

      {/* Card-Detail-Modal — oeffnet sich wenn store.selectedLeadId != null */}
      <CardDetailModal />

      {/* Create-Lead-Modal — oeffnet sich via store.openCreateModal aus Plus-Button (Plan 03) */}
      <CreateLeadModal />

      {/* BulkActionBar — erscheint sticky am unteren Rand wenn Karten selektiert (Plan 04) */}
      <BulkActionBar />
    </div>
  );
}
